import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { getRemoteStream, hangup, initWebRTC, subscribe, toggleMute, unsubscribe } from '../services/webrtcClient';

export default function AgentPhone({ contact, onClose }) {
    const [customerNumber, setCustomerNumber] = useState(contact?.phone || '');
    const [status, setStatus] = useState('Idle');
    const [inCall, setInCall] = useState(false);
    const [muted, setMuted] = useState(false);
    const [duration, setDuration] = useState('00:00');
    const [callStart, setCallStart] = useState(null);
    const [currentCallId, setCurrentCallId] = useState(null);
    const audioRef = useRef();

    useEffect(() => {
        const username = process.env.REACT_APP_WEBRTC_USERNAME;
        const password = process.env.REACT_APP_WEBRTC_PASSWORD;

        // Request microphone permission first
        navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
                // Stop the test stream - we just needed permission
                stream.getTracks().forEach(track => track.stop());
            })
            .catch(() => {
                toast.error('Microphone access required for calling');
            });

        // If build-time credentials are provided, initialize immediately.
        // Otherwise we'll initialize dynamically when the server returns credentials on start.
        if (username && password) {
            initWebRTC({ username, password });
        }

        const id = Date.now().toString();

        subscribe(id, (event, data) => {
            if (event === 'status') setStatus(data);
            if (event === 'connected') {
                setInCall(data);
                if (data) {
                    setCallStart(Date.now());
                } else {
                    setCallStart(null);
                    setDuration('00:00');
                }
            }
            if (event === 'remoteStream') {
                if (audioRef.current && data) {
                    try {
                        console.log('🎵 Setting up remote audio stream:', data);

                        // Stop any existing stream tracks
                        if (audioRef.current.srcObject) {
                            const oldStream = audioRef.current.srcObject;
                            oldStream.getTracks().forEach(track => {
                                track.stop();
                            });
                        }

                        // Set the new stream
                        audioRef.current.srcObject = data;

                        // Ensure audio element is ready
                        audioRef.current.volume = 1.0;
                        audioRef.current.muted = false;

                        // Play the audio - handle autoplay restrictions
                        const playPromise = audioRef.current.play();
                        if (playPromise !== undefined) {
                            playPromise
                                .then(() => {
                                    console.log('✅ Remote audio playing successfully');
                                })
                                .catch((error) => {
                                    console.warn('Audio play failed, trying again:', error);
                                    // Try again after a short delay
                                    setTimeout(() => {
                                        audioRef.current.play().catch((err) => {
                                            console.error('Audio play failed after retry:', err);
                                            toast.error('Unable to play audio. Please check your browser audio settings.');
                                        });
                                    }, 500);
                                });
                        }
                    } catch (e) {
                        console.error('Failed to set up audio stream:', e);
                        toast.error('Failed to set up audio: ' + (e.message || 'Unknown error'));
                    }
                } else {
                    console.warn('Audio element or stream data not available');
                }
            }
            if (event === 'error') {
                toast.error(data?.message || 'WebRTC error');
            }
        });

        return () => unsubscribe(id);
    }, []);

    useEffect(() => {
        if (!callStart) return;

        const id = setInterval(() => {
            const s = Math.floor((Date.now() - callStart) / 1000);
            const m = String(Math.floor(s / 60)).padStart(2, '0');
            const sec = String(s % 60).padStart(2, '0');
            setDuration(`${m}:${sec}`);
        }, 1000);

        return () => clearInterval(id);
    }, [callStart]);

    // Fallback: Periodically check for remote stream if call is active but no stream received
    useEffect(() => {
        if (!inCall || !audioRef.current) return;

        let checkCount = 0;
        const maxChecks = 10; // Check for 5 seconds (10 * 500ms)
        let streamReceived = false;

        const checkInterval = setInterval(() => {
            checkCount++;

            // Check if we already have a stream
            if (audioRef.current && audioRef.current.srcObject) {
                streamReceived = true;
                clearInterval(checkInterval);
                return;
            }

            // Try to get stream from webrtcClient
            try {
                const remoteStream = getRemoteStream();
                if (remoteStream && !streamReceived) {
                    console.log('📞 Fallback: Got remote stream via getRemoteStream()');
                    // Emit the stream event manually
                    if (audioRef.current) {
                        try {
                            if (audioRef.current.srcObject) {
                                audioRef.current.srcObject.getTracks().forEach(track => track.stop());
                            }
                            audioRef.current.srcObject = remoteStream;
                            audioRef.current.play().catch(err => {
                                console.warn('Audio play failed in fallback:', err);
                            });
                            streamReceived = true;
                        } catch (e) {
                            console.error('Failed to set stream in fallback:', e);
                        }
                    }
                }
            } catch (e) {
                // Error getting stream, continue checking
            }

            // If we've checked enough times, stop
            if (checkCount >= maxChecks) {
                clearInterval(checkInterval);
                if (!streamReceived) {
                    console.warn('⚠️ Remote stream not received after multiple checks');
                }
            }
        }, 500);

        return () => clearInterval(checkInterval);
    }, [inCall]);


    const startCall = async () => {
        if (!customerNumber && !contact?.id) {
            toast.error('Enter customer number or select a contact');
            return;
        }

        setStatus('Dialing customer...');
        try {
            const payload = contact?.id
                ? { contactId: contact.id }
                : { customerNumber };

            const response = await api.post('/manual-calls/start', payload);

            // Server returns { success: true, call: { id, ... }, webrtcCredentials }
            if (response.data && response.data.success) {
                toast.success('Call initiated', { icon: '📞' });
                setStatus('Waiting for agent registration...');

                const callId = response.data.call?.id || response.data.callId;
                if (callId) setCurrentCallId(callId);

                // If server returned WebRTC credentials, initialize the Telnyx client with them
                const creds = response.data.webrtcCredentials || response.data.webrtc_credentials || null;
                if (creds && creds.username && creds.password) {
                    initWebRTC({ username: creds.username, password: creds.password });

                    // If server DID NOT already create the agent leg (legBId), notify server agent-ready.
                    // manual-calls.start creates both legs immediately, so skip agent-ready in that case.
                    const serverCreatedAgentLeg = !!response.data.legBId || !!response.data.legB;
                    if (!serverCreatedAgentLeg && callId) {
                        try {
                            await api.post(`/manual-calls/${callId}/agent-ready`);
                            setStatus('Connecting to customer...');
                        } catch (readyErr) {
                            console.warn('Failed to notify server agent-ready:', readyErr);
                            setStatus('Waiting for server to create call...');
                        }
                    } else {
                        setStatus('Waiting for incoming WebRTC call...');
                    }
                } else {
                    // If no creds returned, let server fallback create the call to the agent (server will use SIP creds)
                    setStatus('Waiting for server to call your WebRTC client...');
                }

            } else {
                throw new Error(response.data.message || response.data.error || 'Failed to start call');
            }
        } catch (err) {
            const errorMsg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to start call';
            toast.error(errorMsg);
            setStatus('Idle');
        }
    };

    const handleMute = () => {
        toggleMute(!muted);
        setMuted(!muted);
    };

    const handleHangup = async () => {
        try {
            // Hangup WebRTC call locally
            hangup();

            // Also hangup via API to ensure both legs are hung up
            if (currentCallId) {
                try {
                    await api.post(`/manual-calls/${currentCallId}/hangup`);
                } catch (apiErr) {
                    // Log but don't fail - WebRTC hangup already happened
                    console.warn('API hangup failed:', apiErr);
                }
            }

            setStatus('Hangup');
            setInCall(false);
            setCallStart(null);
            setDuration('00:00');
            setCurrentCallId(null);
        } catch (err) {
            console.error('Hangup error:', err);
            // Still reset UI state
            setStatus('Hangup');
            setInCall(false);
            setCallStart(null);
            setDuration('00:00');
            setCurrentCallId(null);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Agent Web Phone</h2>
                {onClose && status === 'Idle' && (
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        ✕
                    </button>
                )}
            </div>

            {/* Contact Info */}
            {contact && (
                <div className="text-center mb-6 bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-center mb-2">
                        <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-2xl font-bold text-blue-600">
                                {contact.firstName?.[0]}{contact.lastName?.[0]}
                            </span>
                        </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                        {contact.firstName} {contact.lastName}
                    </h3>
                    <p className="text-gray-600 font-mono">{contact.phone}</p>
                    {contact.company && (
                        <p className="text-sm text-gray-500 mt-1">{contact.company}</p>
                    )}
                </div>
            )}

            {/* Status */}
            <div className="text-center mb-6">
                <div>Status: <strong>{status}</strong></div>
                {inCall && <div className="mt-2">Duration: {duration}</div>}
            </div>

            {/* Call Controls */}
            <div className="flex items-center justify-center space-x-4 mb-6">
                {!inCall && (
                    <>
                        {!contact && (
                            <input
                                placeholder="+1..."
                                value={customerNumber}
                                onChange={e => setCustomerNumber(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                        )}
                        <button
                            onClick={startCall}
                            className="flex items-center px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors shadow-lg"
                        >
                            📞 Call Customer
                        </button>
                    </>
                )}

                {inCall && (
                    <>
                        <button
                            onClick={handleMute}
                            className={`flex items-center px-4 py-3 rounded-full transition-colors ${
                                muted
                                    ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                            }`}
                        >
                            {muted ? '🔇 Unmute' : '🎤 Mute'}
                        </button>
                        <button
                            onClick={handleHangup}
                            className="flex items-center px-6 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-lg"
                        >
                            📴 Hangup
                        </button>
                    </>
                )}
            </div>

            {/* Audio element for remote stream */}
            <audio
                ref={audioRef}
                autoPlay
                playsInline
                className="hidden"
            />
        </div>
    );
}
